import {
  DOCUMENT,
  enableProdMode,
  Injector,
  NgModuleRef,
  NgZone,
  provideZoneChangeDetection,
} from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { PlatformNavigation } from './app/platform-navigation';
import { ArcService } from './app/services';
import { NavBreadcrumbsService } from './app/services/nav-breadcrumbs.service';
import { PlatformNavService } from './app/services/platform-nav.service';
import { environment } from './environments/environment';
import { environmentLoader as environmentLoaderPromise } from './environments/environmentLoader';
import { loadStaticHeader } from './environments/staticheader';
import MenuSelectedEventData = PlatformNavigation.MenuSelectedEventData;

export interface AuthenticationObject {
  profileComponent: any;
  onSignInCallback: any;
  trusteapi: any;
}

let appModuleRef: NgModuleRef<AppModule> | null = null;
let shouldInitNavigation: boolean = false;
let userInfoLoaded: boolean = false;
let navigationInitialized: boolean = false;

function initNavigationIfReady() {
  if (navigationInitialized || !shouldInitNavigation || !userInfoLoaded) {
    return;
  }

  navigationInitialized = true;
  initNavigation();
}

function bootstrapApp(): Promise<NgModuleRef<AppModule>> {
  if (appModuleRef) {
    return Promise.resolve(appModuleRef);
  }
  return platformBrowserDynamic([{ provide: ArcService, useValue: arcService }])
    .bootstrapModule(AppModule, {
      applicationProviders: [
        provideZoneChangeDetection(),
        { provide: NgZone, useValue: ngZone },
      ],
    })
    .then((moduleRef) => {
      appModuleRef = moduleRef;
      return moduleRef;
    });
}

function injectProviderJs() {
  const scriptTag = document.createElement('script');
  scriptTag.async = true;
  scriptTag.src = environment.providerJsURL;
  scriptTag.type = 'text/javascript';
  const scripts = document.getElementsByTagName('script');
  if (scripts.length > 0) {
    const firstScript = scripts.item(0);
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(scriptTag, firstScript);
    }
  }
}

function registerOnSignInCallback() {
  const authObj = <AuthenticationObject>(<any>window);
  authObj.onSignInCallback = onSignInCallback;
}

function onSignInCallback(response: any) {
  if (response.signedIn) {
    console.log('authResult received in onSignInCallback');
    const authObj = <any>window;
    const trusteapi = authObj.trusteapi;
    if (trusteapi) {
      console.log('trustapi works!');
      environment.token = response.token.token;
      trusteapi.user.getUserInfo((userInfo: any) => {
        environment.userInfo = userInfo;
        environment.trusteapi = trusteapi;
        loadStaticHeader();
        userInfoLoaded = true;
        initNavigationIfReady();
      });
    }
  }
}

const ngZone = new NgZone({});
const injector = Injector.create({
  providers: [
    { provide: ArcService, useClass: ArcService, deps: [DOCUMENT, NgZone] },
    { provide: DOCUMENT, useValue: document },
    { provide: NgZone, useValue: ngZone },
  ],
});
const arcService = injector.get(ArcService);

environmentLoaderPromise.then((env) => {
  if (environment.production) {
    enableProdMode();
    environment.apiEndpoint = env.apiEndpoint;
    environment.providerJsURL = env.providerJsURL;
    environment.tagServerApiEndpoint = env.tagServerApiEndpoint;
    environment.enableDebug = env.enableDebug;
    environment.auditTrailApiEndpoint = env.auditTrailApiEndpoint;
    environment.baseUrl = env.baseUrl;
    environment.arcUrl = env.arcUrl;
    environment.arcSubdirectory = env.arcSubdirectory;
    environment.arcEnabled = env.arcEnabled;
  }

  arcService.init(() => {
    shouldInitNavigation = true;
    onSignInCallback((window as any).trusteapi.auth);
  });

  if (!arcService.fromArc) {
    registerOnSignInCallback();

    window.platformNavConfig = {
      appContainerId: 'main-container',
      onReady: (): boolean | void => {
        if (arcService.arcEnabledByUser) {
          const success = arcService.redirectToArc();
          if (success) {
            return false;
          }
        }

        shouldInitNavigation = true;
        initNavigationIfReady();
      },
    };

    // For Cypress (no provider.js), bootstrap immediately
    if ((window as any)['Cypress']) {
      bootstrapApp();
    } else {
      // Inject provider.js which will call onSignInCallback to bootstrap with auth
      injectProviderJs();
    }
  }
});

function initNavigation() {
  bootstrapApp().then((moduleRef) => {
    const navBreadcrumbsService = moduleRef.injector.get(NavBreadcrumbsService);
    const platformNavService = moduleRef.injector.get(PlatformNavService);

    window.trustArcPlatformNavigation.on(
      'menuSelected',
      ({ item }: MenuSelectedEventData) => {
        platformNavService.setNext(item.url || '');
      },
    );
    navBreadcrumbsService.isReady();
  });
}
